import { BaseService } from "@/services/BaseService";
import {
  StaffSchedule,
  CreateStaffScheduleDto,
  UpdateStaffScheduleDto,
  StaffAvailability,
  StaffAvailabilityCheck,
  StaffAvailabilityRange,
  ScheduleStats,
  DayOffRequest,
  CreateDayOffRequestDto,
  ReviewDayOffRequestDto,
} from "@/types/staff-schedule";

interface IStaffScheduleService {
  getSchedulesByHotel(hotelId: string): Promise<StaffSchedule[]>;
  getScheduleById(id: string): Promise<StaffSchedule>;
  createSchedule(data: CreateStaffScheduleDto): Promise<StaffSchedule>;
  updateSchedule(
    id: string,
    data: UpdateStaffScheduleDto,
  ): Promise<StaffSchedule>;
  deleteSchedule(id: string): Promise<void>;
  getAvailabilityByHotel(
    hotelId: string,
    date: string,
  ): Promise<StaffAvailability[]>;
  getAvailabilityRange(
    hotelId: string,
    startDate: string,
    endDate: string,
  ): Promise<StaffAvailabilityRange[]>;
  getAvailabilityByStaff(
    staffId: string,
    hotelId: string,
    date: string,
  ): Promise<StaffAvailabilityCheck>;
  getDayOffRequestsByHotel(
    hotelId: string,
    status?: string,
  ): Promise<DayOffRequest[]>;
  getDayOffRequestById(id: string): Promise<DayOffRequest>;
  createDayOffRequest(data: CreateDayOffRequestDto): Promise<DayOffRequest>;
  reviewDayOffRequest(
    id: string,
    data: ReviewDayOffRequestDto,
  ): Promise<DayOffRequest>;
  cancelDayOffRequest(id: string): Promise<DayOffRequest>;
  getStats(hotelId: string, date: string): Promise<ScheduleStats>;
  getMySchedules(hotelId: string): Promise<StaffSchedule[]>;
  getMyDayOffRequests(): Promise<DayOffRequest[]>;
}

class StaffScheduleService
  extends BaseService
  implements IStaffScheduleService
{
  private basePath = "/staff-schedule";
  private mutationOptions = { disableToast: true } as const;

  async getSchedulesByHotel(hotelId: string): Promise<StaffSchedule[]> {
    return this.get<StaffSchedule[]>(`${this.basePath}/hotel/${hotelId}`);
  }

  async getScheduleById(id: string): Promise<StaffSchedule> {
    return this.get<StaffSchedule>(`${this.basePath}/${id}`);
  }

  async createSchedule(data: CreateStaffScheduleDto): Promise<StaffSchedule> {
    return this.post<StaffSchedule>(this.basePath, data, this.mutationOptions);
  }

  async updateSchedule(
    id: string,
    data: UpdateStaffScheduleDto,
  ): Promise<StaffSchedule> {
    return this.patch<StaffSchedule>(
      `${this.basePath}/${id}`,
      data,
      this.mutationOptions,
    );
  }

  async deleteSchedule(id: string): Promise<void> {
    return this.delete<void>(
      `${this.basePath}/${id}`,
      undefined,
      this.mutationOptions,
    );
  }

  async getAvailabilityByHotel(
    hotelId: string,
    date: string,
  ): Promise<StaffAvailability[]> {
    return this.get<StaffAvailability[]>(
      `${this.basePath}/availability/hotel/${hotelId}`,
      { date },
    );
  }

  async getAvailabilityRange(
    hotelId: string,
    startDate: string,
    endDate: string,
  ): Promise<StaffAvailabilityRange[]> {
    return this.get<StaffAvailabilityRange[]>(
      `${this.basePath}/availability/hotel/${hotelId}/range`,
      { startDate, endDate },
    );
  }

  async getAvailabilityByStaff(
    staffId: string,
    hotelId: string,
    date: string,
  ): Promise<StaffAvailabilityCheck> {
    return this.get<StaffAvailabilityCheck>(
      `${this.basePath}/availability/staff/${staffId}/hotel/${hotelId}`,
      { date },
    );
  }

  async getDayOffRequestsByHotel(
    hotelId: string,
    status?: string,
  ): Promise<DayOffRequest[]> {
    return this.get<DayOffRequest[]>(
      `${this.basePath}/day-off/hotel/${hotelId}`,
      status ? { status } : undefined,
    );
  }

  async getDayOffRequestById(id: string): Promise<DayOffRequest> {
    return this.get<DayOffRequest>(`${this.basePath}/day-off/${id}`);
  }

  async createDayOffRequest(
    data: CreateDayOffRequestDto,
  ): Promise<DayOffRequest> {
    return this.post<DayOffRequest>(
      `${this.basePath}/day-off`,
      data,
      this.mutationOptions,
    );
  }

  async reviewDayOffRequest(
    id: string,
    data: ReviewDayOffRequestDto,
  ): Promise<DayOffRequest> {
    return this.patch<DayOffRequest>(
      `${this.basePath}/day-off/${id}/review`,
      data,
      this.mutationOptions,
    );
  }

  async cancelDayOffRequest(id: string): Promise<DayOffRequest> {
    return this.patch<DayOffRequest>(
      `${this.basePath}/day-off/${id}/cancel`,
      {},
      this.mutationOptions,
    );
  }

  async getStats(hotelId: string, date: string): Promise<ScheduleStats> {
    return this.get<ScheduleStats>(`${this.basePath}/stats/hotel/${hotelId}`, {
      date,
    });
  }

  async getMySchedules(hotelId: string): Promise<StaffSchedule[]> {
    return this.get<StaffSchedule[]>(`${this.basePath}/my/hotel/${hotelId}`);
  }

  async getMyDayOffRequests(): Promise<DayOffRequest[]> {
    return this.get<DayOffRequest[]>(`${this.basePath}/day-off/my`);
  }
}

export const staffScheduleService = new StaffScheduleService();
